import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CheckCircle2, XCircle, MinusCircle, Trophy, Home, TrendingUp } from "lucide-react";
import LatexText from "@/components/LatexText";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

const DIFF_COLORS = { easy: "#057642", medium: "#0A66C2", hard: "#CC1016" };

export default function ExamResultPage() {
  const { testId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [questions, setQuestions] = useState({}); // qid -> question with correct

  useEffect(() => {
    (async () => {
      try {
        // Find my session for this test
        const { data: tests } = await api.get("/exams/portal/tests");
        const t = tests.find(x => x.id === testId);
        if (!t) return nav("/exam");
        // Fetch full session
        const sessions = await api.get("/exams/sessions", { params: {} }).catch(() => ({ data: [] }));
        // We need session by test — fetch via /portal to know id. Portal only returns summary. Load by known route
        // Use portal_tests response and re-fetch via /exams/sessions/{sid} once we get sid from last-known
        // But portal doesn't return session_id. Solution: use /tests/{id}/leaderboard to find our row + then session by name/id lookup.
        // Easier: fetch a lightweight sessions listing (not exposed). We'll try a direct list.
        const { data: lb } = await api.get(`/exams/tests/${testId}/leaderboard`);
        setLeaderboard(lb);
        // Find our own row via /auth/me then match student_name; fallback to first
        const { data: me } = await api.get("/auth/me");
        const myRow = lb.find(r => r.student_name === me.name) || lb[0];
        // Not enough — we need full session_id. Instead call /exams/sessions/{sid} which we don't have.
        // Workaround: create/reuse endpoint start returns session; but this is post-submit. Query sessions collection via a helper:
        // Add a helper: /exams/portal/session/{test_id} would be nice. For now use start with 400 fallback:
        // Actually the session was created during attempt; we can query by fetching from an admin listing.
        // Simplest: use the test's leaderboard + /exams/sessions/list?test=... We don't have that. So call a new endpoint we'll add: /exams/portal/my-session/{test_id}
        const { data: mySession } = await api.get(`/exams/portal/my-session/${testId}`);
        setSession(mySession);

        // Load full questions for answers view
        const allIds = mySession.sections.flatMap(s => s.questions.map(q => q.id));
        if (allIds.length) {
          const map = {};
          for (const qid of allIds) {
            const q = mySession.sections.flatMap(s => s.questions).find(x => x.id === qid);
            map[qid] = q;
          }
          setQuestions(map);
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
      }
    })();
    // eslint-disable-next-line
  }, [testId]);

  if (!session) return <div className="p-10 text-[#00000099]">Loading result…</div>;

  const subjectData = Object.entries(session.subject_break || {}).map(([k, v]) => ({
    name: k, score: v.score, total: v.total, pct: v.total ? Math.round(v.score / v.total * 100) : 0,
  }));
  const diffData = Object.entries(session.difficulty_break || {}).map(([k, v]) => ({
    name: k, correct: v.c, total: v.t,
  }));

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      <header className="bg-[#0A66C2] text-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center font-bold">E</div>
            <div>
              <div className="text-[15px] font-bold">Exam Result</div>
              <div className="text-[11px] text-white/75">{session.test_name}</div>
            </div>
          </div>
          <Link to="/exam" className="text-xs px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 inline-flex items-center gap-1"><Home size={12}/> Back to portal</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Score hero */}
        <div className="linkedin-card p-8 text-center" data-testid="result-hero">
          <div className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center text-white text-3xl font-bold ${session.passed ? 'bg-[#057642]' : 'bg-[#CC1016]'}`}>
            {session.percent}%
          </div>
          <div className="text-2xl font-semibold text-[#000000E6] mt-4">{session.passed ? "Passed 🎉" : "Not Passed"}</div>
          <div className="text-sm text-[#00000099] mt-1">You scored {session.score} / {session.total_marks} marks</div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Rank" value={`#${session.rank}`} icon={Trophy} color="#915907" testid="stat-rank"/>
            <Stat label="Percentile" value={`${session.percentile}%`} icon={TrendingUp} color="#0A66C2" testid="stat-percentile"/>
            <Stat label="Correct" value={session.correct_count} icon={CheckCircle2} color="#057642" testid="stat-correct"/>
            <Stat label="Incorrect" value={session.incorrect_count} icon={XCircle} color="#CC1016" testid="stat-incorrect"/>
          </div>
        </div>

        {/* Subject-wise */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="linkedin-card p-5">
            <h3 className="font-semibold mb-3">Subject-wise performance</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={subjectData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDC"/>
                <XAxis dataKey="name" stroke="#00000099" tick={{fontSize: 12}}/>
                <YAxis stroke="#00000099" tick={{fontSize: 12}} domain={[0, 100]}/>
                <Tooltip contentStyle={{background:"#fff", border:"1px solid #E0DFDC", borderRadius:8}}/>
                <Bar dataKey="pct" name="Score %" fill="#0A66C2" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="linkedin-card p-5">
            <h3 className="font-semibold mb-3">Difficulty analysis (correct / total)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={diffData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDC"/>
                <XAxis dataKey="name" stroke="#00000099" tick={{fontSize: 12}}/>
                <YAxis stroke="#00000099" tick={{fontSize: 12}}/>
                <Tooltip contentStyle={{background:"#fff", border:"1px solid #E0DFDC", borderRadius:8}}/>
                <Bar dataKey="correct" name="Correct" radius={[6,6,0,0]}>
                  {diffData.map((d, i) => <Cell key={i} fill={DIFF_COLORS[d.name] || "#0A66C2"} />)}
                </Bar>
                <Bar dataKey="total" name="Total" fill="#E0DFDC" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="linkedin-card p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Trophy size={16} className="text-[#915907]"/> Leaderboard (top 10)</h3>
          <div className="divide-y divide-[#E0DFDC]" data-testid="result-leaderboard">
            {leaderboard.slice(0, 10).map(r => (
              <div key={r.rank} className={`py-2.5 flex items-center gap-3 ${r.student_name === session.student_name ? 'bg-[#EAF3FB] -mx-2 px-2 rounded' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${r.rank === 1 ? 'bg-[#F5C300]/20 text-[#915907]' : r.rank === 2 ? 'bg-[#C0C0C0]/30 text-[#666]' : r.rank === 3 ? 'bg-[#CD7F32]/20 text-[#915907]' : 'bg-[#EDEBE7] text-[#00000099]'}`}>{r.rank}</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{r.student_name}{r.student_name === session.student_name ? " (You)" : ""}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-[#0A66C2]">{r.percent}%</div>
                  <div className="text-[10px] text-[#00000099]">{r.score} marks</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Answer review */}
        <div className="linkedin-card p-5">
          <h3 className="font-semibold mb-3">Answer review</h3>
          <div className="space-y-3">
            {(session.per_question || []).map((pq, i) => {
              const q = questions[pq.question_id];
              if (!q) return null;
              const icon = pq.status === "correct" ? <CheckCircle2 size={16} className="text-[#057642]"/> : pq.status === "incorrect" ? <XCircle size={16} className="text-[#CC1016]"/> : <MinusCircle size={16} className="text-[#00000066]"/>;
              return (
                <div key={pq.question_id} className="p-3 rounded-md border border-[#E0DFDC]" data-testid={`review-${i}`}>
                  <div className="flex items-start gap-2">
                    {icon}
                    <div className="flex-1">
                      <div className="text-sm font-semibold"><LatexText>{q.text}</LatexText></div>
                      <div className="text-xs mt-1">
                        <span className="text-[#00000099]">Your answer: </span>
                        <span className="text-[#000000E6]">{Array.isArray(pq.chosen) ? pq.chosen.join(", ") : (pq.chosen || <em className="text-[#00000066]">Not attempted</em>)}</span>
                      </div>
                      {pq.status !== "correct" && (
                        <div className="text-xs mt-1">
                          <span className="text-[#00000099]">Correct: </span>
                          <span className="text-[#057642] font-semibold">{Array.isArray(pq.correct) ? pq.correct.join(", ") : pq.correct}</span>
                        </div>
                      )}
                      <div className="text-[10px] mt-1 text-[#00000099]">{q.subject} · {q.difficulty} · Marks: {pq.marks_awarded}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="p-3 rounded-md bg-[#F4F2EE]" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#00000099] font-semibold uppercase tracking-wider">
        <Icon size={12} style={{color}}/> {label}
      </div>
      <div className="text-xl font-bold mt-1" style={{color}}>{value}</div>
    </div>
  );
}
